import { useEffect, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import API from "@/lib/api";

/**
 * Capabilities checklist — renders the 10 toggleable admin permissions
 * grouped by category. Used by:
 *   - CreateEmployeeModal (when role=admin) — all caps pre-checked
 *   - EditUserModal (when target is admin and viewer is super-admin)
 *
 * Props:
 *   value: string[]            — currently-selected capability keys
 *   onChange: (string[]) => void
 *   disabled?: boolean
 */
export default function CapabilitiesChecklist({ value, onChange, disabled = false }) {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    API.get("/admin/capabilities/catalog")
      .then(({ data }) => { if (alive) setCatalog(data); })
      .catch(() => { if (alive) setCatalog({ capabilities: [], defaults_on_create: [] }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const toggle = (key) => {
    if (disabled) return;
    const set = new Set(value || []);
    if (set.has(key)) set.delete(key); else set.add(key);
    onChange(Array.from(set));
  };

  const checkAll = () => {
    if (disabled || !catalog) return;
    onChange(catalog.capabilities.map((c) => c.key));
  };
  const checkNone = () => {
    if (disabled) return;
    onChange([]);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400 py-3">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading permissions…
      </div>
    );
  }

  // Group by category for visual organization
  const byCat = {};
  for (const cap of catalog?.capabilities || []) {
    if (!byCat[cap.category]) byCat[cap.category] = [];
    byCat[cap.category].push(cap);
  }
  const selected = new Set(value || []);
  const total = (catalog?.capabilities || []).length;

  return (
    <div className="rounded-xl border border-purple-200 dark:border-purple-800/60 bg-purple-50/50 dark:bg-purple-950/20 p-4 space-y-3" data-testid="capabilities-checklist">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-purple-600 dark:text-purple-300" />
          <p className="text-sm font-semibold text-purple-900 dark:text-purple-200">
            Permissions <span className="text-xs font-normal text-purple-600 dark:text-purple-400">({selected.size}/{total})</span>
          </p>
        </div>
        {!disabled && (
          <div className="flex items-center gap-1 text-xs">
            <button
              type="button"
              onClick={checkAll}
              className="px-2 py-0.5 rounded-full bg-white dark:bg-slate-800 border border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-200 hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors"
              data-testid="caps-check-all"
            >
              Check all
            </button>
            <button
              type="button"
              onClick={checkNone}
              className="px-2 py-0.5 rounded-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              data-testid="caps-uncheck-all"
            >
              Uncheck all
            </button>
          </div>
        )}
      </div>
      <div className="space-y-3">
        {Object.entries(byCat).map(([cat, caps]) => (
          <div key={cat}>
            <p className="text-[10px] font-bold uppercase tracking-wider text-purple-700 dark:text-purple-300/80 mb-1.5">{cat}</p>
            <div className="space-y-1.5">
              {caps.map((cap) => {
                const on = selected.has(cap.key);
                return (
                  <label
                    key={cap.key}
                    className={`flex items-start gap-2.5 p-2 rounded-lg cursor-pointer transition-colors border ${
                      on
                        ? "bg-white dark:bg-slate-900 border-purple-300 dark:border-purple-700/60"
                        : "bg-white/40 dark:bg-slate-900/30 border-transparent hover:bg-white/70 dark:hover:bg-slate-900/50"
                    } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
                    data-testid={`cap-row-${cap.key}`}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggle(cap.key)}
                      disabled={disabled}
                      className="mt-0.5 w-4 h-4 accent-purple-600"
                      data-testid={`cap-checkbox-${cap.key}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{cap.label}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{cap.description}</p>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {disabled && (
        <p className="text-[11px] italic text-amber-700 dark:text-amber-300">
          Only a super-admin can edit these. Ask the founder account (GF000001) to make changes.
        </p>
      )}
    </div>
  );
}
